import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import api from '../../services/api';

/* ───────── animations ───────── */
const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;
const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 6px 2px rgba(76, 175, 80, 0.4); }
  50% { box-shadow: 0 0 14px 5px rgba(76, 175, 80, 0.7); }
`;
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;
const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;
const gentleSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(218, 165, 32, 0.15); }
  50% { box-shadow: 0 0 40px rgba(218, 165, 32, 0.3), 0 0 60px rgba(255, 215, 0, 0.1); }
`;

/* ───────── layout ───────── */
const Page = styled.div`
  min-height: calc(100vh - 80px);
  background: #FFFDF7;
  position: relative;
  overflow: hidden;
`;

const GoldOrnament = styled.div`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  opacity: 0.04;
  &:nth-child(1) { width: 600px; height: 600px; background: radial-gradient(circle, #DAA520, transparent 70%); top: -200px; right: -200px; }
  &:nth-child(2) { width: 500px; height: 500px; background: radial-gradient(circle, #FFD700, transparent 70%); bottom: -150px; left: -150px; }
  &:nth-child(3) { width: 300px; height: 300px; background: radial-gradient(circle, #B8860B, transparent 70%); top: 50%; left: 50%; transform: translate(-50%, -50%); }
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem 3rem;

  @media (max-width: 600px) {
    padding: 1rem 1rem 2rem;
  }
`;

/* ───────── hero ───────── */
const Hero = styled.section`
  text-align: center;
  padding: 3rem 0 2rem;
  animation: ${slideUp} 0.8s ease-out;

  @media (max-width: 600px) {
    padding: 1.5rem 0 1rem;
  }
`;

const HeroTitle = styled.h1`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 800;
  letter-spacing: -0.5px;
  margin: 0;
  color: #2C1810;
  line-height: 1.2;

  span {
    background: linear-gradient(135deg, #B8860B 0%, #DAA520 30%, #FFD700 50%, #DAA520 70%, #8B6914 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 5s linear infinite;
  }
`;

const HeroSub = styled.p`
  color: #6B5744;
  font-size: clamp(0.9rem, 2vw, 1.15rem);
  margin: 1rem auto 0;
  max-width: 560px;
  line-height: 1.7;
  padding: 0 0.5rem;
`;

/* ───────── mandala hero ornament ───────── */
const MandalaContainer = styled.div`
  position: relative;
  width: 220px;
  height: 220px;
  margin: 2rem auto;

  @media (max-width: 600px) {
    width: 160px;
    height: 160px;
    margin: 1.5rem auto;
  }
`;

const MandalaImg = styled.img`
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 0 30px rgba(218, 165, 32, 0.2));
`;

const OrbitRing = styled.div`
  position: absolute;
  inset: -25px;
  border: 1px solid rgba(218, 165, 32, 0.12);
  border-radius: 50%;
  animation: ${gentleSpin} 90s linear infinite;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #DAA520;
    opacity: 0.5;
  }
  &::before { top: -3px; left: 50%; transform: translateX(-50%); }
  &::after { bottom: -3px; left: 50%; transform: translateX(-50%); }

  @media (max-width: 600px) {
    inset: -18px;
  }
`;

const OrbitRingOuter = styled(OrbitRing)`
  inset: -50px;
  border-color: rgba(218, 165, 32, 0.06);
  animation-duration: 120s;
  animation-direction: reverse;

  @media (max-width: 600px) {
    inset: -35px;
  }
`;

/* ───────── product cards ───────── */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  animation: ${slideUp} 1s ease-out 0.3s both;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  @media (max-width: 380px) {
    grid-template-columns: 1fr;
  }
`;

const ElegantCard = styled.div`
  background: #FFFFFF;
  border: 1px solid rgba(218, 165, 32, 0.1);
  border-radius: 16px;
  padding: 2rem 1.5rem;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(.25,.8,.25,1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, ${p => p.$accent || '#DAA520'}, transparent);
    opacity: 0;
    transition: opacity 0.35s;
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 40px rgba(139, 105, 20, 0.1);
    border-color: ${p => p.$accent || '#DAA520'}44;
    &::before { opacity: 1; }
    .card-icon { animation: ${float} 2s ease-in-out infinite; }
  }

  @media (max-width: 600px) {
    padding: 1.2rem 1rem;
    border-radius: 12px;
  }
`;

const CardIcon = styled.div.attrs({ className: 'card-icon' })`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: ${p => p.$bg || '#FFF8E7'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin-bottom: 1rem;

  @media (max-width: 600px) {
    width: 44px;
    height: 44px;
    font-size: 1.4rem;
    border-radius: 10px;
    margin-bottom: 0.6rem;
  }
`;

const CardTitle = styled.h3`
  color: #2C1810;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0 0 0.5rem;

  @media (max-width: 600px) {
    font-size: 0.9rem;
    margin: 0 0 0.3rem;
  }
`;

const CardDesc = styled.p`
  color: #9B8B7A;
  font-size: 0.88rem;
  line-height: 1.6;
  margin: 0;

  @media (max-width: 600px) {
    font-size: 0.72rem;
    line-height: 1.4;
  }
`;

const LiveDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4caf50;
  margin-right: 6px;
  vertical-align: middle;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const Badge = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${p => p.$bg || '#DAA520'};
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 600px) {
    top: 0.6rem;
    right: 0.6rem;
    font-size: 0.55rem;
    padding: 2px 7px;
  }
`;

/* ───────── section heading ───────── */
const SectionTitle = styled.h2`
  text-align: center;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(1.4rem, 3vw, 2rem);
  font-weight: 700;
  color: #2C1810;
  margin: 3.5rem 0 0.5rem;

  @media (max-width: 600px) {
    margin: 2.5rem 0 0.3rem;
  }
`;

const SectionSub = styled.p`
  text-align: center;
  color: #9B8B7A;
  font-size: 0.9rem;
  margin: 0 0 2rem;

  @media (max-width: 600px) {
    font-size: 0.8rem;
    margin: 0 0 1rem;
  }
`;

const GoldDivider = styled.div`
  width: 60px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #DAA520, transparent);
  margin: 0.5rem auto 2rem;

  @media (max-width: 600px) {
    margin: 0.3rem auto 1rem;
  }
`;

/* ───────── top astrologer cards ───────── */
const AstrologerRow = styled.div`
  display: flex;
  gap: 1.5rem;
  overflow-x: auto;
  padding: 0.5rem 0 1rem;
  animation: ${slideUp} 1s ease-out 0.5s both;
  scrollbar-width: thin;
  scrollbar-color: rgba(218, 165, 32, 0.2) transparent;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: rgba(218, 165, 32, 0.2); border-radius: 4px; }

  @media (max-width: 600px) {
    gap: 1rem;
  }
`;

const AstroCard = styled.div`
  flex: 0 0 180px;
  background: #FFFFFF;
  border: 1px solid rgba(218, 165, 32, 0.1);
  border-radius: 16px;
  padding: 1.5rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(.25,.8,.25,1);

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 8px 30px rgba(218, 165, 32, 0.12);
    border-color: rgba(218, 165, 32, 0.3);
  }

  @media (max-width: 600px) {
    flex: 0 0 140px;
    padding: 1rem 0.8rem;
  }
`;

const AstroAvatar = styled.div`
  width: 64px;
  height: 80px;
  border-radius: 8px;
  margin: 0 auto 0.75rem;
  background: linear-gradient(135deg, #DAA520, #B8860B);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: 700;
  color: #fff;
  animation: ${glowPulse} 3s ease-in-out infinite;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    border-radius: 8px;
    object-fit: cover;
  }

  @media (max-width: 600px) {
    width: 52px;
    height: 65px;
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
`;

const StatusDot = styled.span`
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => p.$online ? '#4caf50' : '#ccc'};
  border: 2px solid #fff;

  @media (max-width: 600px) {
    width: 8px;
    height: 8px;
    bottom: 3px;
    right: 3px;
  }
`;

const AstroName = styled.p`
  color: #2C1810;
  font-weight: 600;
  font-size: 0.85rem;
  margin: 0 0 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 600px) {
    font-size: 0.75rem;
  }
`;

const AstroSpec = styled.p`
  color: #9B8B7A;
  font-size: 0.72rem;
  margin: 0 0 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 600px) {
    font-size: 0.65rem;
  }
`;

const AstroRate = styled.p`
  color: #4caf50;
  font-weight: 700;
  font-size: 0.8rem;
  margin: 0.4rem 0 0;

  @media (max-width: 600px) {
    font-size: 0.7rem;
  }
`;

const AstroRating = styled.span`
  color: #DAA520;
  font-size: 0.75rem;
  display: block;
  margin-top: 0.2rem;

  @media (max-width: 600px) {
    font-size: 0.65rem;
  }
`;

const ViewAllBtn = styled.button`
  display: block;
  margin: 1.5rem auto 0;
  padding: 10px 32px;
  border-radius: 10px;
  border: 1.5px solid #DAA520;
  background: transparent;
  color: #8B6914;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s;
  font-family: 'Inter', Arial, sans-serif;

  &:hover {
    background: linear-gradient(135deg, #DAA520, #B8860B);
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(218, 165, 32, 0.3);
  }

  @media (max-width: 600px) {
    padding: 8px 24px;
    font-size: 0.8rem;
  }
`;

/* ───────── vedic tools section ───────── */
const ToolsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
  animation: ${slideUp} 1s ease-out 0.5s both;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
  @media (max-width: 380px) {
    grid-template-columns: 1fr;
  }
`;

const ToolItem = styled.div`
  background: #FFFFFF;
  border: 1px solid rgba(218, 165, 32, 0.1);
  border-radius: 14px;
  padding: 1.25rem 1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(.25,.8,.25,1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, ${p => p.$accent || '#DAA520'}, transparent);
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 28px rgba(139, 105, 20, 0.1);
    border-color: ${p => p.$accent || '#DAA520'}44;
    &::before { opacity: 1; }
  }

  @media (max-width: 600px) {
    padding: 1rem 0.8rem;
    border-radius: 10px;
  }
`;

const ToolItemIcon = styled.div`
  font-size: 1.6rem;
  margin-bottom: 0.5rem;
  @media (max-width: 600px) { font-size: 1.3rem; margin-bottom: 0.3rem; }
`;

const ToolItemName = styled.div`
  color: #2C1810;
  font-weight: 700;
  font-size: 0.92rem;
  margin-bottom: 0.3rem;
  @media (max-width: 600px) { font-size: 0.8rem; }
`;

const ToolItemDesc = styled.div`
  color: #9B8B7A;
  font-size: 0.78rem;
  line-height: 1.4;
  @media (max-width: 600px) { font-size: 0.7rem; }
`;

const ToolBadge = styled.span`
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  background: ${p => p.$bg || '#DAA520'};
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ToolItemDimmed = styled(ToolItem)`
  opacity: 0.45;
  cursor: default;
  pointer-events: none;
  &:hover {
    transform: none;
    box-shadow: none;
    border-color: rgba(218, 165, 32, 0.1);
    &::before { opacity: 0; }
  }
`;

const ComingSoonLabel = styled.h3`
  color: #9B8B7A;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin: 2rem 0 1rem;
  font-weight: 600;
  @media (max-width: 600px) { font-size: 0.75rem; margin: 1.5rem 0 0.75rem; }
`;

/* ───────── data ───────── */
const PRODUCTS = [
  {
    icon: '📞',
    title: 'Talk to Astrologer',
    desc: 'Instant chat, voice & video calls with expert astrologers. Get guidance in real-time.',
    path: '/talk-to-astrologer',
    accent: '#4caf50',
    iconBg: '#E8F5E9',
    badge: null,
    live: true,
  },
  {
    icon: '📅',
    title: 'Book Consultation',
    desc: 'Tarot, palm reading, reiki & more. Schedule with a specialist.',
    path: '/consultation',
    accent: '#2196f3',
    iconBg: '#E3F2FD',
    badge: null,
  },
  {
    icon: '⭐',
    title: 'Daily Nakshatra Prediction',
    desc: 'Nakshatra-based daily predictions for all 12 rasis.',
    path: '#',
    accent: '#555',
    iconBg: '#F3E5F5',
    badge: 'Soon',
  },
];

const VEDIC_TOOLS_ACTIVE = [
  { icon: '📜', name: 'Free Horoscope', desc: 'Full birth chart with planets, dashas, yogas & AV analysis', path: '/free-kundli', accent: '#f7971e', badge: 'Free' },
  { icon: '🕉️', name: 'Today Panchangam', desc: 'Tithi, Nakshatra, Yoga, Karana & Vara for today', path: '/today-panchangam', accent: '#4caf50', badge: 'Free' },
];

const VEDIC_TOOLS_COMING_SOON = [
  { icon: '⚡', name: 'KalaShakti Daily Balance', desc: 'Personalized 0-100 daily score with hourly & weekly forecast', accent: '#9c27b0' },
  { icon: '🔱', name: 'KalaSakthi V2', desc: 'Life blueprint — 12 house scores, 5-category radar & drill-down', accent: '#e91e63' },
  { icon: '🕐', name: 'Daily Timings', desc: 'Rahu Kalam, Yamagandam, Gulika & Choghadiya', accent: '#e65100' },
  { icon: '🪐', name: 'Planetary Hora', desc: '24 planetary hours — know which planet rules each hour', accent: '#1565c0' },
  { icon: '✨', name: 'Abhijit Muhurta', desc: 'The most auspicious 48-minute golden window at noon', accent: '#ffc107' },
  { icon: '📅', name: 'Muhurta Checker', desc: 'Score any date for marriage, travel, business & more', accent: '#2e7d32' },
  { icon: '💍', name: 'Horoscope Matching', desc: 'Ashtakoot gun milan — 8 kootas, 36-point North Indian system', accent: '#e91e63' },
  { icon: '💑', name: '10 Porutham', desc: 'Tamil marriage compatibility — Rajju, Vedha, Nadi & more', accent: '#ad1457' },
  { icon: '🎯', name: 'KP Chart', desc: 'Krishnamurti Paddhati — Sub-Lord analysis, Placidus houses', accent: '#00bcd4' },
];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || '').toUpperCase() + (parts[1]?.[0] || '').toUpperCase();
};

/* ───────── component ───────── */
const LandingPage = () => {
  const navigate = useNavigate();
  const [topAstrologers, setTopAstrologers] = useState([]);
  useEffect(() => {
    api.get('/astrologers')
      .then(res => {
        const data = res.data?.astrologers || res.data || [];
        const list = Array.isArray(data) ? data : [];
        const sorted = [...list].sort((a, b) => {
          if ((b.rating || 0) !== (a.rating || 0)) return (b.rating || 0) - (a.rating || 0);
          return (b.experience_years || 0) - (a.experience_years || 0);
        });
        setTopAstrologers(sorted.slice(0, 8));
      })
      .catch(() => {});
  }, []);

  return (
    <Page>
      <GoldOrnament /><GoldOrnament /><GoldOrnament />

      <Content>
        {/* Hero */}
        <Hero>
          <HeroTitle>
            Decode Your <span>Cosmic Blueprint</span>
          </HeroTitle>
          <HeroSub>
            Ancient wisdom meets modern guidance — consult India's top Vedic astrologers,
            explore your kundli, and unlock spiritual clarity in one place.
          </HeroSub>

          {/* Mandala ornament using the logo icon */}
          <MandalaContainer>
            <OrbitRingOuter />
            <OrbitRing />
            <MandalaImg src="/images/JyotishSastra_Icon.svg" alt="JyotishSastra" />
          </MandalaContainer>
        </Hero>

        {/* Product grid */}
        <Grid>
          {PRODUCTS.map((p, i) => (
            <ElegantCard
              key={i}
              $accent={p.accent}
              onClick={() => p.path !== '#' && navigate(p.path)}
              style={p.path === '#' ? { opacity: 0.5, cursor: 'default' } : {}}
            >
              {p.badge && <Badge $bg={p.accent}>{p.badge}</Badge>}
              <CardIcon $bg={p.iconBg}>{p.icon}</CardIcon>
              <CardTitle>
                {p.live && <LiveDot />}
                {p.title}
              </CardTitle>
              <CardDesc>{p.desc}</CardDesc>
            </ElegantCard>
          ))}
        </Grid>

        {/* Vedic Astro Tools */}
        <SectionTitle>Vedic Astro Tools</SectionTitle>
        <GoldDivider />
        <SectionSub>Free horoscope, panchangam & more Vedic tools</SectionSub>
        <ToolsGrid>
          {VEDIC_TOOLS_ACTIVE.map((t) => (
            <ToolItem key={t.name} $accent={t.accent} onClick={() => navigate(t.path)}>
              {t.badge && <ToolBadge $bg={t.accent}>{t.badge}</ToolBadge>}
              <ToolItemIcon>{t.icon}</ToolItemIcon>
              <ToolItemName>{t.name}</ToolItemName>
              <ToolItemDesc>{t.desc}</ToolItemDesc>
            </ToolItem>
          ))}
        </ToolsGrid>
        <ComingSoonLabel>Coming Soon</ComingSoonLabel>
        <ToolsGrid>
          {VEDIC_TOOLS_COMING_SOON.map((t) => (
            <ToolItemDimmed key={t.name} $accent={t.accent}>
              <ToolBadge $bg="#999">Soon</ToolBadge>
              <ToolItemIcon>{t.icon}</ToolItemIcon>
              <ToolItemName>{t.name}</ToolItemName>
              <ToolItemDesc>{t.desc}</ToolItemDesc>
            </ToolItemDimmed>
          ))}
        </ToolsGrid>

        {/* Top Astrologers */}
        {topAstrologers.length > 0 && (
          <>
            <SectionTitle>Top Astrologers</SectionTitle>
            <GoldDivider />
            <SectionSub>Consult with our highest-rated experts</SectionSub>
            <AstrologerRow>
              {topAstrologers.map(a => {
                const name = a.name || a.user?.name || 'Astrologer';
                const initials = getInitials(name);
                const isOnline = a.online_status === 'online' || a.is_online;
                const rate = a.price_per_minute_chat || a.price_per_minute_call || 10;
                return (
                  <AstroCard key={a.id} onClick={() => navigate(`/astrologer/${a.id}`)}>
                    <AstroAvatar>
                      {a.profile_image ? (
                        <img src={a.profile_image} alt={name} />
                      ) : (
                        initials
                      )}
                      <StatusDot $online={isOnline} />
                    </AstroAvatar>
                    <AstroName>{name}</AstroName>
                    <AstroSpec>{a.specialization || a.expertise?.split(',')[0] || 'Astrology'}</AstroSpec>
                    {a.rating > 0 && (
                      <AstroRating>{'★'.repeat(Math.round(a.rating))} {a.rating.toFixed(1)}</AstroRating>
                    )}
                    <AstroRate>₹{rate}/min</AstroRate>
                  </AstroCard>
                );
              })}
            </AstrologerRow>
            <ViewAllBtn onClick={() => navigate('/talk-to-astrologer')}>
              View All Astrologers →
            </ViewAllBtn>
          </>
        )}

      </Content>
    </Page>
  );
};

export default LandingPage;
